import { BREAKPOINTS } from "@ubnt/store.core/lib/constants/layout";
import React from "react";
import styled, { css } from "styled-components";

import Image from "util/Image";

const Section = styled.div`
  padding-bottom: 30px;
  min-height: 400px;
  overflow-x: auto;
`;

const Body = styled.div`
  max-width: 1160px;
  margin-left: auto;
  margin-right: auto;
  min-width: 600px;
`;

const CellContent = styled.div``;

const header_cell = css`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.neutral11};
  font-weight: 700;
  min-height: auto;
  line-height: 1.4;
  padding-bottom: 15px;

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    font-size: 12px;
  }

  ${CellContent} {
    padding-left: 28px;
    display: inline-flex;
    background-position: 0 50%;
    background-repeat: no-repeat;
    margin-left: auto;
    margin-right: auto;
    align-items: center;
    gap: 6px;
    line-height: 1.25;
  }
`;
const note_cell = css`
  border-top: 1px solid ${({ theme }) => theme.colors.neutral02};
  font-size: 14px;
  line-height: 1.4;
  text-align: center;

  color: ${({ theme }) => theme.colors.text2};
  min-height: 50px;
  @media (max-width: ${(props) => props.theme.Breakingpoint.sm}px) {
    font-size: 12px;
  }
  sup {
    color: ${({ theme }) => theme.colors.ublue06};
    padding-right: 4px;
  }
`;

const Cell = styled.div`
  flex: 0 0 ${100 / 5}%;
  max-width: ${100 / 5}%;
  flex-direction: column;
  display: flex;
  justify-content: center;
  text-align: center;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 5px;
  padding-right: 5px;
  min-height: 90px;
  position: relative;
  text-align: center;
  ${(props) => css`
    ${props.headerCell && header_cell};
    ${props.noteCell && note_cell};
  `}
  &:first-child {
    border-radius: 5px 0 0 5px;
  }
  &:last-child {
    border-radius: 0 5px 5px 0;
  }
  img,
  svg {
    margin-left: auto;
    margin-right: auto;
    vertical-align: middle;
    height: auto;
  }
  &:before {
    display: block;
    content: " ";
    position: absolute;
    width: 3px;
    top: 0;
    bottom: 0;
    left: -1px;
    background-color: ${({ theme }) => theme.colors.neutral00};
  }
`;

const Row = styled.div`
  display: flex;
  flex-wrap: nowrap;
  position: relative;
  &:nth-child(2n + 2) {
    ${Cell} {
      background-color: ${({ theme }) => theme.colors.neutral02};
    }
  }
  &:last-child {
    ${Cell} {
      background-color: transparent;
    }
  }
`;

const CellLabel = styled.div`
  font-size: 13px;
  line-height: ${17 / 13};
  text-align: center;

  color: ${({ theme }) => theme.colors.text2};
  font-weight: 700;
  @media (max-width: ${(props) => props.theme.Breakingpoint.sm}px) {
    font-size: 12px;
  }
`;

const DeviceTitle = styled.div`
  padding-bottom: 2px;
`;

const DeviceOsReq = styled.div`
  font-size: 12px;
  text-align: center;

  color: ${({ theme }) => theme.colors.text2};
`;

const DeviceImage = styled.div`
  padding-top: 10px;
  padding-bottom: 5px;
  img {
    width: 100%;
  }
`;

const CheckMark = styled.div`
  text-align: center;
`;

const CheckMarkContainer = styled.div`
  display: inline-block;
  vertical-align: middle;
  position: relative;
`;

const CheckMarkNumber = styled.div`
  position: absolute;
  right: -8px;
  top: 2px;
  color: ${({ theme }) => theme.colors.ublue06};
  font-size: 10px;
  line-height: 1;
`;

const check = (number) => {
  if (number === false) {
    return (
      <CheckMark>
        <CheckMarkContainer>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M0.516615 0.516616C-0.172206 1.20544 -0.172205 2.32224 0.516616 3.01106L5.50559 8.00003L0.516635 12.989C-0.172186 13.6778 -0.172186 14.7946 0.516635 15.4834C1.20546 16.1723 2.32226 16.1723 3.01108 15.4834L8.00003 10.4945L12.9888 15.4833C13.6776 16.1721 14.7944 16.1721 15.4833 15.4833C16.1721 14.7944 16.1721 13.6776 15.4833 12.9888L10.4945 8.00003L15.4833 3.01123C16.1721 2.32241 16.1721 1.20561 15.4833 0.516786C14.7945 -0.172035 13.6777 -0.172034 12.9888 0.516786L8.00003 5.50559L3.01106 0.516616C2.32224 -0.172205 1.20544 -0.172205 0.516615 0.516616Z"
              fill="#8A8A8B"
            />
          </svg>
        </CheckMarkContainer>
      </CheckMark>
    );
  }
  return (
    <CheckMark>
      <CheckMarkContainer>
        <svg
          width="20"
          height="16"
          viewBox="0 0 20 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2 8L8.5 14L17.5 2"
            stroke="#006FFF"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {typeof number === "number" && number !== 0 && (
          <CheckMarkNumber>{number}</CheckMarkNumber>
        )}
      </CheckMarkContainer>
    </CheckMark>
  );
};

const HardwareRequirements = ({ title, table }) => {
  return (
    <Section>
      <Body>
        {table.map((row, index) => (
          <Row key={index}>
            {row.map((col, index) => (
              <Cell
                noteCell={col.legend ? true : null}
                titleCell={col.title ? true : null}
                headerCell={col.header ? true : null}
                key={index}
              >
                {col.title && col.header && (
                  <CellContent>
                    {col.icon && <img src={col.icon} alt={col.title} />}
                    {col.title}
                  </CellContent>
                )}
                {col.title && !col.header && (
                  <CellLabel>
                    <DeviceTitle>{col.title}</DeviceTitle>
                    <DeviceOsReq>UniFi OS {col.os}</DeviceOsReq>
                    <DeviceImage style={col.imageStyle ? col.imageStyle : null}>
                      <Image src={col.image} />
                    </DeviceImage>
                  </CellLabel>
                )}
                {typeof col.legend === "string" && (
                  <CellContent
                    dangerouslySetInnerHTML={{
                      __html: col.legend,
                    }}
                  />
                )}
                {typeof col.value !== "undefined" && check(col.value)}
              </Cell>
            ))}
          </Row>
        ))}
      </Body>
    </Section>
  );
};

export default HardwareRequirements;
